import { HttpClient, HttpContext } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { Observable, map, switchMap, iif, defer, of } from "rxjs";
import { OptmisticSse } from "src/app/backend/decorators/optmistic-sse.decorator";
import {
  Good,
  GoodLight,
  GoodType,
  GoodUnit,
  GoodUpdate
} from "src/app/backend/models/warehouse/good";
import {
  Movement,
  MovementCreate,
  MovementGood,
  MovementLight,
  MovementReason,
  MovementTransferCreate
} from "src/app/backend/models/warehouse/movements";
import {
  Supplier,
  SupplierCreate,
  SupplierLight,
  SupplierUpdate
} from "src/app/backend/models/warehouse/supplier";
import {
  WarehouseStorage,
  WarehouseStorageCreate,
  WarehouseStorageLight,
  WarehouseStorageUpdate
} from "src/app/backend/models/warehouse/warehouse-storage";
import {
  HTTP_CONTEXT_TOKEN_NO_CACHE,
  HTTP_CONTEXT_TOKEN_NO_SWAP_DEEP_PROPERTY_ID
} from "src/app/core/http-context-tokens";
import { SseEvents } from "src/app/sse/sse-events";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import { first, isUndefined, omitBy } from "lodash-es";
import {
  BusinessUnit,
  BusinessUnitCreate,
  BusinessUnitLight,
  BusinessUnitUpdate
} from "src/app/backend/models/warehouse/business-unit";
import {
  omitFalsyProperties,
  omitUndefinedAndNullProperties
} from "src/app/support/object";
import { WarehouseSending } from "src/app/backend/models/warehouse/sending";
import { sntTranslate } from "src/app/support/translate";

@Injectable()
export class WarehouseBackend {
  private readonly http = inject(HttpClient);

  loadSupplier(id: string): Observable<Supplier> {
    return this.http.get<Supplier>(
      `${environment.snoots.api.directory}/suppliers/${id}`
    );
  }

  loadSupplierLight(id: string): Observable<SupplierLight> {
    return this.http.get<SupplierLight>(
      `${environment.snoots.api.directory}/suppliers/${id}`,
      {
        params: {
          light: true
        }
      }
    );
  }

  @OptmisticSse({
    factories: [
      {
        event: SseEvents.SUPPLIER_CREATED,
        payloadFactory: ({
          args,
          response
        }: {
          args?: any[];
          response?: Supplier;
        }) => {
          return response;
        }
      }
    ]
  })
  @UserNotify({
    successMessage: marker("Fornitore creato"),
    errorMessage: marker("Impossibile creare il fornitore")
  })
  createSupplier(payload: SupplierCreate): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.directory}/suppliers`,
      payload
    );
  }

  @OptmisticSse({
    factories: [
      {
        event: SseEvents.SUPPLIER_DELETED,
        payloadFactory: ({ args }: { args?: any[] }) => {
          return args![0];
        }
      }
    ]
  })
  @UserNotify({
    successMessage: marker("Fornitore rimosso"),
    errorMessage: marker("Impossibile rimuovere")
  })
  deleteSupplier(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.directory}/suppliers/${id}`
    );
  }

  @OptmisticSse({
    factories: [
      {
        event: SseEvents.SUPPLIER_UPDATED,
        payloadFactory: ({ args }) => {
          return <SupplierUpdate>args![1];
        }
      }
    ]
  })
  @UserNotify({
    successMessage: marker("Fornitore aggiornato"),
    errorMessage: marker("Impossibile aggiornare")
  })
  updateSupplier(id: string, payload: SupplierUpdate): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.directory}/suppliers/${id}`,
      payload
    );
  }

  listSuppliersLightAll(): Observable<SupplierLight[]> {
    return this.listSuppliersLight({ page: 1, pageSize: 10000 }).pipe(
      map((response) => response.docs)
    );
  }

  listSuppliersLight(
    pageRequest: PageRequest
  ): Observable<PaginatedResult<SupplierLight>> {
    return this.http.get<PaginatedResult<SupplierLight>>(
      `${environment.snoots.api.directory}/suppliers`,
      {
        params: omitUndefinedAndNullProperties({
          light: true,
          p: pageRequest.page,
          s: pageRequest.pageSize
        })
      }
    );
  }

  loadStorage(id: string): Observable<WarehouseStorage> {
    return this.http.get<WarehouseStorage>(
      `${environment.snoots.api.warehouse}/storages/${id}`
    );
  }

  loadStorageLight(id: string): Observable<WarehouseStorageLight> {
    return this.http.get<WarehouseStorageLight>(
      `${environment.snoots.api.warehouse}/storages/${id}`,
      {
        params: {
          light: true
        }
      }
    );
  }

  @OptmisticSse({
    factories: [
      {
        event: SseEvents.STORAGE_CREATED,
        payloadFactory: ({
          response
        }: {
          args?: any[];
          response?: WarehouseStorage;
        }) => response
      }
    ]
  })
  @UserNotify({
    successMessage: marker("Storage creato"),
    errorMessage: marker("Impossibile creare")
  })
  createStorage(payload: WarehouseStorageCreate): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.warehouse}/storages`,
      payload
    );
  }

  loadRetail(): Observable<WarehouseStorageLight | undefined> {
    return this.listStoragesLightAll().pipe(
      map((storages: WarehouseStorageLight[]) =>
        storages.find((x) => !!x.retail)
      )
    );
  }

  listStoragesLightAll(): Observable<WarehouseStorageLight[]> {
    return this.listStoragesLight({ page: 1, pageSize: 10000 }).pipe(
      map((response) => response.docs)
    );
  }

  listStoragesLight(
    pageRequest: PageRequest
  ): Observable<PaginatedResult<WarehouseStorageLight>> {
    return this.http.get<PaginatedResult<WarehouseStorageLight>>(
      `${environment.snoots.api.warehouse}/storages`,
      {
        params: omitUndefinedAndNullProperties({
          light: true,
          p: pageRequest.page,
          s: pageRequest.pageSize
        })
      }
    );
  }

  @OptmisticSse({
    factories: [
      {
        event: SseEvents.STORAGE_DELETED,
        payloadFactory: ({ args }: { args?: any[] }) => {
          return args![0];
        }
      }
    ]
  })
  @UserNotify({
    successMessage: marker("Storage rimosso"),
    errorMessage: marker("Impossibile rimuovere")
  })
  deleteStorage(
    id: string,
    payload: {
      destinationStorageId?: string;
    }
  ): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.warehouse}/storages/${id}/delete`,
      payload
    );
  }

  @OptmisticSse({
    factories: [
      {
        event: SseEvents.GOOD_UPDATED,
        payloadFactory: ({ args }) => {
          return <WarehouseStorageUpdate>args![1];
        }
      }
    ]
  })
  @UserNotify({
    successMessage: marker("Articolo aggiornato"),
    errorMessage: marker("Impossibile aggiornare")
  })
  updateGood(
    id: string,
    payload: Partial<GoodUpdate> & Pick<GoodUpdate, "version">
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.warehouse}/goods/${id}`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Articolo eliminato"),
    errorMessage: marker("Impossibile eliminare l'articolo")
  })
  deleteGood(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.warehouse}/goods/${id}`
    );
  }

  @OptmisticSse({
    factories: [
      {
        event: SseEvents.STORAGE_UPDATED,
        payloadFactory: ({ args }) => {
          return <WarehouseStorageUpdate>args![1];
        }
      }
    ]
  })
  @UserNotify({
    successMessage: marker("Storage aggiornato"),
    errorMessage: marker("Impossibile aggiornare")
  })
  updateStorage(id: string, payload: WarehouseStorageUpdate): Observable<void> {
    const { retail, ...rest } = payload;

    return this.http
      .patch<void>(`${environment.snoots.api.warehouse}/storages/${id}`, rest)
      .pipe(
        switchMap(() => {
          return iif(
            () => !isUndefined(retail) && !!retail,
            defer(() => {
              return this.http.put<void>(
                `${environment.snoots.api.warehouse}/storages/${id}/enable-retail`,
                {}
              );
            }),
            of(void 0)
          );
        })
      );
  }

  @UserNotify({
    successMessage: marker("Carico creato"),
    errorMessage: marker("Impossibile creare")
  })
  createLoad(payload: MovementCreate): Observable<Movement> {
    return this.createMovementSilently(payload);
  }

  @UserNotify({
    successMessage: marker("Magazzino aggiornato"),
    errorMessage: marker("Impossibile aggiornare il magazzino")
  })
  createSelling(payload: MovementCreate): Observable<Movement> {
    return this.createMovementSilently(payload);
  }

  createTransfer(payload: MovementTransferCreate): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.warehouse}/transfers`,
      payload,
      {
        context: new HttpContext().set(
          HTTP_CONTEXT_TOKEN_NO_SWAP_DEEP_PROPERTY_ID,
          true
        )
      }
    );
  }

  createMovementSilently(payload: MovementCreate): Observable<Movement> {
    return this.http.post<Movement>(
      `${environment.snoots.api.warehouse}/movements`,
      payload,
      {
        context: new HttpContext().set(
          HTTP_CONTEXT_TOKEN_NO_SWAP_DEEP_PROPERTY_ID,
          true
        )
      }
    );
  }

  loadGood(id: string): Observable<Good> {
    return this.http
      .get<Good>(`${environment.snoots.api.warehouse}/goods/${id}`)
      .pipe(map((good) => extendGood(good)));
  }

  loadGoodNoCache(id: string): Observable<Good> {
    return this.http
      .get<Good>(`${environment.snoots.api.warehouse}/goods/${id}`, {
        context: new HttpContext().set(HTTP_CONTEXT_TOKEN_NO_CACHE, true)
      })
      .pipe(map((good) => extendGood(good)));
  }

  loadGoodLightNoCache(id: string): Observable<GoodLight> {
    return this.loadGoodLight(
      id,
      new HttpContext().set(HTTP_CONTEXT_TOKEN_NO_CACHE, true)
    );
  }

  loadGoodLight(id: string, context?: HttpContext): Observable<GoodLight> {
    return this.http.get<GoodLight>(
      `${environment.snoots.api.warehouse}/goods/${id}`,
      {
        params: {
          light: true
        },
        context
      }
    );
  }

  listGoodsLightByTipeAndCode({
    type,
    minsan,
    gtin,
    storageId
  }: {
    type: GoodType;
    minsan?: string;
    gtin?: string;
    storageId?: string;
  }): Observable<GoodLight[]> {
    return this.listGoodsLight({
      page: 1,
      pageSize: 10000,
      filterValues: omitFalsyProperties({ type, minsan, gtin, storageId })
    }).pipe(
      map((response) => {
        return response.docs;
      })
    );
  }

  findGoodLightByTipeAndCode({
    type,
    minsan,
    gtin
  }: {
    type: GoodType;
    minsan?: string;
    gtin?: string;
  }): Observable<GoodLight | undefined> {
    return this.listGoodsLight({
      page: 1,
      pageSize: 10000,
      filterValues: omitFalsyProperties({ type, minsan, gtin })
    }).pipe(
      map((response) => {
        return first(response.docs);
      })
    );
  }

  findGoodLight({
    type,
    minsan,
    gtin,
    unit
  }: {
    type: GoodType;
    minsan?: string;
    gtin?: string;
    unit?: GoodUnit;
  }): Observable<GoodLight | undefined> {
    return this.listGoodsLight({
      filterValues: omitFalsyProperties({ type, minsan, gtin, unit })
    }).pipe(
      map((response) => {
        return first(response.docs);
      })
    );
  }

  listGoods(
    pageRequest: PageRequest & { select?: string | undefined }
  ): Observable<PaginatedResult<Good>> {
    return this.http
      .get<PaginatedResult<Good>>(`${environment.snoots.api.warehouse}/goods`, {
        params: omitUndefinedAndNullProperties({
          p: pageRequest.page,
          s: pageRequest.pageSize,
          ...pageRequest.filterValues,
          select: pageRequest.select
        })
      })
      .pipe(
        map((response) => {
          return {
            ...response,
            docs: response.docs.map(extendGood)
          };
        })
      );
  }

  listGoodsLight(
    pageRequest: PageRequest,
    filters?: {
      storageId?: string;
      name?: string;
      type?: GoodType;
      minsan?: string;
      gtin?: string;
    }
  ): Observable<PaginatedResult<GoodLight>> {
    return this.http.get<PaginatedResult<Good>>(
      `${environment.snoots.api.warehouse}/goods`,
      {
        params: omitFalsyProperties({
          light: true,
          p: pageRequest.page,
          s: pageRequest.pageSize,
          storageId: filters?.storageId,
          name: filters?.name,
          type: filters?.type,
          minsan: filters?.minsan,
          gtin: filters?.gtin,
          ...pageRequest.filterValues
        })
      }
    );
  }

  listGoodsByStorage(
    storageId: string,
    pageRequest: PageRequest,
    orderBy?: keyof Good | "_id",
    sort?: "desc" | "asc"
  ): Observable<PaginatedResult<Good>> {
    return this.http.get<PaginatedResult<Good>>(
      `${environment.snoots.api.warehouse}/goods`,
      {
        params: omitUndefinedAndNullProperties({
          orderBy: orderBy || "_id",
          sort: sort || "desc",
          p: pageRequest.page,
          s: pageRequest.pageSize,
          storageId: storageId,
          ...pageRequest.filterValues
        })
      }
    );
  }

  loadMovement(id: string): Observable<Movement> {
    return this.http.get<MovementLight>(
      `${environment.snoots.api.warehouse}/movements/${id}`
    );
  }

  loadMovementLight(id: string): Observable<MovementLight> {
    return this.http.get<MovementLight>(
      `${environment.snoots.api.warehouse}/movements/${id}`,
      {
        params: {
          light: true
        }
      }
    );
  }

  listMovements(
    request: PageRequest & { select?: string | undefined }
  ): Observable<PaginatedResult<Movement>> {
    return this.http.get<PaginatedResult<Movement>>(
      `${environment.snoots.api.warehouse}/movements`,
      {
        params: omitUndefinedAndNullProperties({
          orderBy: "_id",
          sort: "desc",
          p: request.page,
          s: request.pageSize,
          ...request.filterValues,
          select: request.select
        })
      }
    );
  }

  listMovementsByStorage(
    storageId: string,
    pageRequest: PageRequest
  ): Observable<PaginatedResult<Movement>> {
    return this.http
      .get<PaginatedResult<Movement>>(
        `${environment.snoots.api.warehouse}/movements`,
        {
          params: omitBy<any>(
            {
              orderBy: "_id",
              sort: "desc",
              p: pageRequest.page,
              s: pageRequest.pageSize,
              storageId: storageId,
              ...pageRequest.filterValues
            },
            isUndefined
          )
        }
      )
      .pipe(
        map((response) => {
          return {
            ...response,
            docs: filterMovementsByGoodUnit(
              response.docs,
              <GoodUnit | undefined>pageRequest.filterValues?.unit
            )
          };
        })
      );
  }

  listMovementsLightByGood({
    gtin,
    minsan,
    storage,
    pageRequest
  }: {
    gtin: string | undefined;
    minsan: string | undefined;
    storage: string | undefined;
    pageRequest: PageRequest;
  }): Observable<PaginatedResult<MovementLight>> {
    return this.http
      .get<PaginatedResult<MovementLight>>(
        `${environment.snoots.api.warehouse}/movements`,
        {
          params: omitBy<any>(
            {
              light: true,
              gtin: gtin,
              minsan: minsan,
              sourceId: storage,
              destinationId: storage,
              orderBy: "_id",
              sort: "desc",
              p: pageRequest.page,
              s: pageRequest.pageSize,
              ...pageRequest.filterValues
            },
            isUndefined
          )
        }
      )
      .pipe(
        map((response) => {
          return {
            ...response,
            docs: response.docs.map(extend)
          };
        })
      );
  }

  listMovementsByGood({
    gtin,
    minsan,
    unit,
    pageRequest
  }: {
    gtin: string | undefined;
    minsan: string | undefined;
    unit: GoodUnit | undefined;
    pageRequest: PageRequest;
  }): Observable<PaginatedResult<Movement>> {
    return this.http
      .get<PaginatedResult<Movement>>(
        `${environment.snoots.api.warehouse}/movements`,
        {
          params: omitBy<any>(
            {
              gtin: gtin,
              minsan: minsan,
              unit: unit,
              orderBy: "_id",
              sort: "desc",
              p: pageRequest.page,
              s: pageRequest.pageSize,
              ...pageRequest.filterValues
            },
            isUndefined
          )
        }
      )
      .pipe(
        map((response) => {
          return {
            ...response,
            docs: filterMovementsByGoodUnit(response.docs.map(extend), unit)
          };
        })
      );
  }

  listSellingMovement(): Observable<WarehouseSending[]> {
    return this.http
      .get<PaginatedResult<WarehouseSending>>(
        `${environment.snoots.api.warehouse}/sendings/movements`,
        {}
      )
      .pipe(map((response) => response.docs));
  }

  listStockAlignment(): Observable<WarehouseSending[]> {
    return this.http
      .get<PaginatedResult<WarehouseSending>>(
        `${environment.snoots.api.warehouse}/sendings/goods`,
        {}
      )
      .pipe(map((response) => response.docs));
  }

  loadSending(id: string): Observable<WarehouseSending> {
    return this.http.get<WarehouseSending>(
      `${environment.snoots.api.warehouse}/sendings/${id}`
    );
  }

  loadSendingLight(id: string): Observable<WarehouseSending> {
    return this.http.get<WarehouseSending>(
      `${environment.snoots.api.warehouse}/sendings/${id}`
    );
  }

  @UserNotify({
    successMessage: sntTranslate(marker("Registrato")),
    errorMessage: sntTranslate(
      marker("Impossibile procedere con la registrazione")
    )
  })
  createSendingMovements(movementIds: string[]): Observable<WarehouseSending> {
    return this.http.post<WarehouseSending>(
      `${environment.snoots.api.warehouse}/sendings/movements`,
      {
        movementIds
      }
    );
  }

  @UserNotify({
    successMessage: sntTranslate(marker("Allineato")),
    errorMessage: sntTranslate(
      marker("Impossibile procedere con l|allineamento")
    )
  })
  createSendingGoods(goodIds: string[]): Observable<WarehouseSending> {
    return this.http.post<WarehouseSending>(
      `${environment.snoots.api.warehouse}/sendings/goods`,
      {
        goodIds
      }
    );
  }

  @UserNotify({
    errorMessage: marker("Impossibile avviare l'esportazione")
  })
  export({
    storageId,
    startAt,
    endAt,
    reason
  }: {
    storageId?: string;
    startAt?: string;
    endAt?: string;
    reason?: MovementReason;
  }): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.warehouse}/movements/exports`,
      omitBy(
        {
          storageId,
          startAt,
          endAt,
          reason
        },
        isUndefined
      )
    );
  }

  @UserNotify({
    errorMessage: marker("Impossibile avviare l'esportazione")
  })
  exportGoods({ type }: { type?: GoodType }): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.warehouse}/goods/exports`,
      omitBy({ type }, isUndefined)
    );
  }

  listBusinessUnitsLightAll(): Observable<BusinessUnitLight[]> {
    return this.listBusinessUnitsLight({ page: 1, pageSize: 10000 }).pipe(
      map((response) => {
        return response.docs;
      })
    );
  }

  listBusinessUnitsLight(
    pageRequest: PageRequest
  ): Observable<PaginatedResult<BusinessUnitLight>> {
    return this.http.get<PaginatedResult<BusinessUnitLight>>(
      `${environment.snoots.api.warehouse}/business-units`,
      {
        params: omitUndefinedAndNullProperties({
          light: true,
          p: pageRequest.page,
          s: pageRequest.pageSize
        })
      }
    );
  }

  loadBusinessUnit(id: string): Observable<BusinessUnit> {
    return this.http.get<BusinessUnit>(
      `${environment.snoots.api.warehouse}/business-units/${id}`
    );
  }

  loadBusinessUnitLight(id: string): Observable<BusinessUnitLight> {
    return this.http.get<BusinessUnitLight>(
      `${environment.snoots.api.warehouse}/business-units/${id}`,
      {
        params: {
          light: true
        }
      }
    );
  }

  @UserNotify({
    successMessage: marker("Unità organizzativa creata"),
    errorMessage: marker("Impossibile creare l'unità organizzativa")
  })
  createBusinessUnit(payload: BusinessUnitCreate): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.warehouse}/business-units`,
      payload
    );
  }

  @OptmisticSse({
    factories: [
      {
        event: SseEvents.BUSINESS_UNIT_DELETED,
        payloadFactory: ({ args }: { args?: any[] }) => {
          return args![0];
        }
      }
    ]
  })
  @UserNotify({
    successMessage: marker("Unità organizzativa rimossa"),
    errorMessage: marker("Impossibile rimuovere l'unità organizzativa")
  })
  deleteBusinessUnit(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.warehouse}/business-units/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Unità organizzativa creata"),
    errorMessage: marker("Impossibile creare l'unità organizzativa")
  })
  updateBusinessUnit({
    id,
    payload
  }: {
    id: string;
    payload: BusinessUnitUpdate;
  }): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.warehouse}/business-units/${id}`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Storno effettuato"),
    errorMessage: marker("Impossibile effettuare lo storno")
  })
  cancellation(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.warehouse}/movements/${id}/cancel`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Storno effettuato"),
    errorMessage: marker("Impossibile effettuare lo storno")
  })
  cancellationGood({
    movementId,
    goodIndex
  }: {
    movementId: string;
    goodIndex: number;
  }): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.warehouse}/movements/${movementId}/goods/${goodIndex}/cancel`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Articolo spacchettato"),
    errorMessage: marker("Impossibile spacchettare l'articolo")
  })
  unpack({
    goodId,
    stockIndex,
    quantity,
    note
  }: {
    goodId: string;
    stockIndex: number;
    quantity: number;
    note?: string;
  }): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.warehouse}/goods/${goodId}/unpack`,
      {
        stockIndex,
        quantity,
        note
      }
    );
  }
}

function extend(movement: Movement): Movement {
  return {
    ...movement,
    quantityByGoodCode: movement.goods.reduce<
      Record<string, number | undefined>
    >((accumulator, good: MovementGood) => {
      accumulator[(good.gtin || good.minsan)!] = good.quantity;
      return accumulator;
    }, {}),
    priceByGoodCode: movement.goods.reduce<Record<string, number | undefined>>(
      (accumulator, good: MovementGood) => {
        accumulator[(good.gtin || good.minsan)!] = good.price;
        return accumulator;
      },
      {}
    ),
    batchNumberByGoodCode: movement.goods.reduce<
      Record<string, string | undefined>
    >((accumulator, good: MovementGood) => {
      accumulator[(good.gtin || good.minsan)!] = good.batchNumber;
      return accumulator;
    }, {}),
    expiresAtByGoodCode: movement.goods.reduce<
      Record<string, string | undefined>
    >((accumulator, good: MovementGood) => {
      accumulator[(good.gtin || good.minsan)!] = good.expiresAt;
      return accumulator;
    }, {})
  };
}

function filterMovementsByGoodUnit(
  movements: Movement[] | undefined,
  goodUnit: GoodUnit | undefined
): Movement[] {
  if (!goodUnit || !movements) {
    return movements || [];
  }

  return movements.filter((movement: Movement) => {
    return !!movement.goods.find(
      (good: MovementGood) => good.unit === goodUnit
    );
  });
}

function extendGood(good: Good): Good {
  return {
    ...good,
    reorderPoint: isUndefined(good.reorderPoint) ? null : good.reorderPoint
  };
}
